import type { CashflowPoint } from '@epc/contracts';
import { useMemo } from 'react';

/**
 * Графики нарисованы вручную в SVG: без сторонних библиотек, одинаково на телефоне и ПК.
 * Пунктир — план бюджета, сплошная — факт; оранжевая линия — сегодня.
 */

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

function niceMax(v: number): number {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  const n = v / p;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return step * p;
}

const fmtAxis = (v: number) => new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(v / 1e6);

export function CumulativeChart({ points, today }: { points: CashflowPoint[]; today: string }) {
  const W = 640;
  const H = 260;
  const pad = { l: 52, r: 16, t: 16, b: 30 };
  const data = useMemo(() => points.filter((p) => p.planCumulative > 0 || p.factCumulative > 0), [points]);
  if (!data.length) {
    return <p className="py-10 text-[14px] text-ink-2">Нет плана оплат: загрузите бюджет-график.</p>;
  }
  const max = niceMax(Math.max(...data.map((p) => Math.max(p.planCumulative, p.factCumulative))));
  // Если план начинается в ближайшие недели, ось начинается с сегодняшней недели — видно «сегодня»
  const first = Date.parse(data[0]!.start);
  const todayMs = Date.parse(today);
  const t0 =
    todayMs < first && first - todayMs < 56 * 86_400_000
      ? todayMs - ((new Date(today).getUTCDay() + 6) % 7) * 86_400_000
      : first;
  const t1 = Date.parse(data[data.length - 1]!.start) + 7 * 86_400_000;
  const x = (d: string | number) =>
    pad.l + (((typeof d === 'string' ? Date.parse(d) : d) - t0) / (t1 - t0)) * (W - pad.l - pad.r);
  const y = (v: number) => pad.t + (1 - v / max) * (H - pad.t - pad.b);
  // Нарастающий итог отмечаем в конце недели
  const endOf = (s: string) => Date.parse(s) + 7 * 86_400_000;
  const planPath = [
    `M${x(t0)},${y(0)}`,
    ...data.map((p) => `L${x(endOf(p.start))},${y(p.planCumulative)}`),
  ].join(' ');
  const todayT = Date.parse(today);
  const factData = data.filter((p) => Date.parse(p.start) <= todayT);
  const factPath = factData.length
    ? [
        `M${x(t0)},${y(0)}`,
        ...factData.map((p) => `L${x(Math.min(endOf(p.start), todayT))},${y(p.factCumulative)}`),
      ].join(' ')
    : null;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((k) => k * max);
  const months: Array<{ t: number; label: string }> = [];
  const d = new Date(t0);
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + 1);
  while (d.getTime() < t1) {
    months.push({ t: d.getTime(), label: MONTHS[d.getUTCMonth()]! });
    d.setUTCMonth(d.getUTCMonth() + 1);
  }
  const showToday = todayT >= t0 && todayT <= t1;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="h-auto w-full"
      role="img"
      aria-label="Оплаты по бюджету нарастающим итогом"
    >
      {ticks.map((v) => (
        <g key={v}>
          <line x1={pad.l} x2={W - pad.r} y1={y(v)} y2={y(v)} stroke="var(--color-line)" />
          <text x={pad.l - 8} y={y(v) + 4} textAnchor="end" className="fill-ink-2 text-[11px]">
            {fmtAxis(v)}
          </text>
        </g>
      ))}
      {months.map((m) => (
        <text key={m.t} x={x(m.t)} y={H - 8} textAnchor="middle" className="fill-ink-2 text-[11px]">
          {m.label}
        </text>
      ))}
      <path d={planPath} fill="none" stroke="var(--color-ink-2)" strokeWidth={2} strokeDasharray="5 5" />
      {factPath ? <path d={factPath} fill="none" stroke="var(--color-ink)" strokeWidth={2.5} /> : null}
      {showToday ? (
        <g>
          <line
            x1={x(todayT)}
            x2={x(todayT)}
            y1={pad.t}
            y2={H - pad.b}
            stroke="var(--color-accent)"
            strokeWidth={1.5}
          />
          <text x={x(todayT) + 6} y={pad.t + 10} className="fill-accent-text text-[11px]">
            сегодня
          </text>
        </g>
      ) : null}
    </svg>
  );
}

export function WeeklyBars({
  points,
  max: forcedMax,
}: {
  points: Array<{ label: string; value: number }>;
  max?: number;
}) {
  const W = 520;
  const H = 220;
  const pad = { l: 44, r: 8, t: 12, b: 34 };
  if (!points.length)
    return <p className="py-10 text-[14px] text-ink-2">Оплат по плану в ближайшие недели нет.</p>;
  const max = niceMax(forcedMax ?? Math.max(...points.map((p) => p.value)));
  const bw = (W - pad.l - pad.r) / points.length;
  const y = (v: number) => pad.t + (1 - v / max) * (H - pad.t - pad.b);
  const ticks = [0, 0.5, 1].map((k) => k * max);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label="Оплаты по неделям, млн ₸">
      {ticks.map((v) => (
        <g key={v}>
          <line x1={pad.l} x2={W - pad.r} y1={y(v)} y2={y(v)} stroke="var(--color-line)" />
          <text x={pad.l - 6} y={y(v) + 4} textAnchor="end" className="fill-ink-2 text-[11px]">
            {fmtAxis(v)}
          </text>
        </g>
      ))}
      {points.map((p, i) => (
        <g key={p.label}>
          <rect
            x={pad.l + i * bw + bw * 0.18}
            y={y(p.value)}
            width={bw * 0.64}
            height={Math.max(0, H - pad.b - y(p.value))}
            fill="var(--color-ink)"
          >
            <title>{`${p.label}: ${fmtAxis(p.value)} млн ₸`}</title>
          </rect>
          <text x={pad.l + i * bw + bw / 2} y={H - 14} textAnchor="middle" className="fill-ink-2 text-[11px]">
            {p.label}
          </text>
        </g>
      ))}
    </svg>
  );
}
